import { supabase } from "@/lib/supabase";

/**
 * Nota sobre el estilo de las consultas.
 *
 * No se usan `select` anidados (`organizations!inner ( ... )`). La inferencia
 * de tipos de supabase-js para recursos embebidos depende de los metadatos de
 * relaciones que emite su propio generador, y los nuestros los genera
 * `scripts/gen-types.mjs`, que no los produce. Consultar por separado y unir
 * en memoria cuesta una peticion mas y queda completamente tipado, que en un
 * panel es mejor negocio que ahorrar un viaje.
 *
 * RLS acota todas estas consultas: no hace falta filtrar por usuario.
 */

export interface Membership {
  organizationId: string;
  role: string;
  name: string;
  slug: string;
  currency: string;
  timezone: string;
  industry: string | null;
  storeName: string | null;
  logoKey: string | null;
  primaryColor: string;
  primaryDomain: string | null;
  /**
   * Tipos que admite el catalogo de esta tienda (0040). Viaja con la membresia
   * porque decide que ofrece el formulario de alta, y pedirlo aparte obligaria
   * a esperar una segunda consulta antes de poder dibujarlo.
   *
   * La barrera de verdad es el trigger products_kind_allowed: esto solo evita
   * ofrecer una opcion que la base va a rechazar.
   */
  catalogKinds: string[];
}

export async function fetchMemberships(): Promise<Membership[]> {
  const { data: members, error } = await supabase
    .from("organization_members")
    .select("organization_id, role")
    .eq("status", "active");

  if (error) throw error;

  const orgIds = (members ?? []).map((m) => m.organization_id);
  if (orgIds.length === 0) return [];

  const [orgs, settings, domains] = await Promise.all([
    supabase
      .from("organizations")
      .select("id, name, slug, currency, timezone, status, industry")
      .in("id", orgIds),
    supabase
      .from("store_settings")
      .select("organization_id, store_name, logo_key, primary_color, catalog_kinds")
      .in("organization_id", orgIds),
    supabase
      .from("store_domains")
      .select("organization_id, domain")
      .in("organization_id", orgIds)
      .eq("is_primary", true),
  ]);

  if (orgs.error) throw orgs.error;

  const orgBy = new Map((orgs.data ?? []).map((o) => [o.id, o]));
  const settingsBy = new Map((settings.data ?? []).map((s) => [s.organization_id, s]));
  const domainBy = new Map((domains.data ?? []).map((d) => [d.organization_id, d.domain]));

  return (members ?? [])
    .flatMap((m) => {
      const org = orgBy.get(m.organization_id);
      if (!org || org.status !== "active") return [];

      const s = settingsBy.get(m.organization_id);
      return [
        {
          organizationId: m.organization_id,
          role: m.role,
          name: org.name,
          slug: org.slug,
          currency: org.currency,
          timezone: org.timezone,
          industry: org.industry,
          storeName: s?.store_name ?? null,
          logoKey: s?.logo_key ?? null,
          primaryColor: s?.primary_color ?? "#0e6a5f",
          primaryDomain: domainBy.get(m.organization_id) ?? null,
          // Sin store_settings la tienda esta a medio dar de alta: se asume lo
          // mismo que el default de la columna, no el catalogo entero.
          catalogKinds: s?.catalog_kinds ?? ["physical"],
        } satisfies Membership,
      ];
    })
    .sort((a, b) => a.name.localeCompare(b.name, "es"));
}

export interface ModuleEntry {
  key: string;
  name: string;
  tier: string;
  sortOrder: number;
}

/**
 * Modulos activos de una organizacion. De aqui sale el menu entero: si un
 * modulo no tiene fila, la seccion no existe para ese cliente.
 */
export async function fetchModules(organizationId: string): Promise<ModuleEntry[]> {
  const { data: active, error } = await supabase
    .from("organization_modules")
    .select("module_key, expires_at")
    .eq("organization_id", organizationId)
    .eq("enabled", true);

  if (error) throw error;

  const now = Date.now();
  const keys = (active ?? [])
    .filter((m) => !m.expires_at || new Date(m.expires_at).getTime() > now)
    .map((m) => m.module_key);

  if (keys.length === 0) return [];

  const { data: modules, error: modErr } = await supabase
    .from("modules")
    .select("key, name, tier, sort_order")
    .in("key", keys)
    .eq("available", true);

  if (modErr) throw modErr;

  return (modules ?? [])
    .map((m) => ({ key: m.key, name: m.name, tier: m.tier, sortOrder: m.sort_order }))
    .sort((a, b) => a.sortOrder - b.sortOrder);
}
